import {
  BarChart3,
  Building2,
  ChartSpline,
  HelpCircle,
  Lightbulb,
  Radio,
  Trophy,
} from "lucide-react";

import type { SuggestedCategoryType } from "@/types/suggestedPrompts";
import type { LucideIcon } from "lucide-react";

type ColorKey = "blue" | "green" | "amber" | "purple";

interface Presentation {
  icon: LucideIcon;
  color: ColorKey;
}

const KEY_PRESENTATION: Record<string, Presentation> = {
  "business-performance": { icon: BarChart3, color: "blue" },
  "channel-performance": { icon: Radio, color: "green" },
  "attribution-faqs": { icon: HelpCircle, color: "amber" },
  "sales-snapshot": { icon: Building2, color: "blue" },
  "growth-signals": { icon: ChartSpline, color: "green" },
  "sku-promotion-impact": { icon: Trophy, color: "amber" },
  "company-deep-dive": { icon: Building2, color: "blue" },
  "market-analysis": { icon: ChartSpline, color: "green" },
  "competitive-landscape": { icon: Trophy, color: "amber" },
  "how-to-use": { icon: Lightbulb, color: "purple" },
};

const TYPE_PRESENTATION: Record<SuggestedCategoryType, Presentation> = {
  metric: { icon: BarChart3, color: "blue" },
  trend: { icon: ChartSpline, color: "green" },
  comparison: { icon: Trophy, color: "amber" },
  entity: { icon: Building2, color: "blue" },
  comms: { icon: Radio, color: "green" },
  faq: { icon: HelpCircle, color: "amber" },
  meta: { icon: Lightbulb, color: "purple" },
  default: { icon: HelpCircle, color: "blue" },
};

const DEFAULT_PRESENTATION: Presentation = {
  icon: HelpCircle,
  color: "blue",
};

export const getCategoryPresentation = (
  key: string,
  type?: SuggestedCategoryType
): Presentation =>
  KEY_PRESENTATION[key] ??
  (type ? TYPE_PRESENTATION[type] : undefined) ??
  DEFAULT_PRESENTATION;

export const COLOR_CLASSES = {
  blue: {
    icon: "text-blue-600 dark:text-blue-400",
    hover:
      "hover:bg-blue-50 hover:border-blue-600 dark:hover:bg-blue-950/30 dark:hover:border-blue-400",
    active:
      "bg-blue-50 border-blue-600 dark:bg-blue-950/30 dark:border-blue-400",
    chevronHover: "group-hover:text-blue-600 dark:group-hover:text-blue-400",
    chevronActive: "text-blue-600 dark:text-blue-400",
  },
  green: {
    icon: "text-green-600 dark:text-green-400",
    hover:
      "hover:bg-green-50 hover:border-green-600 dark:hover:bg-green-950/30 dark:hover:border-green-400",
    active:
      "bg-green-50 border-green-600 dark:bg-green-950/30 dark:border-green-400",
    chevronHover: "group-hover:text-green-600 dark:group-hover:text-green-400",
    chevronActive: "text-green-600 dark:text-green-400",
  },
  amber: {
    icon: "text-amber-600 dark:text-amber-400",
    hover:
      "hover:bg-amber-50 hover:border-amber-600 dark:hover:bg-amber-950/30 dark:hover:border-amber-400",
    active:
      "bg-amber-50 border-amber-600 dark:bg-amber-950/30 dark:border-amber-400",
    chevronHover: "group-hover:text-amber-600 dark:group-hover:text-amber-400",
    chevronActive: "text-amber-600 dark:text-amber-400",
  },
  purple: {
    icon: "text-purple-600 dark:text-purple-400",
    hover:
      "hover:bg-purple-50 hover:border-purple-600 dark:hover:bg-purple-950/30 dark:hover:border-purple-400",
    active:
      "bg-purple-50 border-purple-600 dark:bg-purple-950/30 dark:border-purple-400",
    chevronHover:
      "group-hover:text-purple-600 dark:group-hover:text-purple-400",
    chevronActive: "text-purple-600 dark:text-purple-400",
  },
} as const;
